import { COSELLER_MIC_COPY } from '../constants/watchSession';

/** One microphone the user can pick for a co-seller join. */
export interface MicrophoneOption {
  deviceId: string;
  label: string;
}

/**
 * The browser gave no usable microphone: access refused (`NotAllowedError`),
 * none present (`NotFoundError`), busy (`NotReadableError`) or no capture API.
 * The user always reads "Microphone access is needed to join with your mic."
 */
export class MicrophoneUnavailableError extends Error {
  constructor(message: string, readonly reason?: unknown) {
    super(message);
    this.name = 'MicrophoneUnavailableError';
  }
}

const errorName = (error: unknown): string => (error as { name?: string } | null)?.name ?? 'unknown error';

/**
 * Open the microphone with `constraints`, or throw MicrophoneUnavailableError.
 * The one place this app asks the browser for the mic on a live call.
 */
export const openMicrophoneStream = async (constraints: MediaTrackConstraints | true): Promise<MediaStream> => {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new MicrophoneUnavailableError('This browser offers no microphone capture (getUserMedia)');
  }
  try {
    return await navigator.mediaDevices.getUserMedia({ audio: constraints });
  } catch (error) {
    throw new MicrophoneUnavailableError(`The browser refused the microphone (${errorName(error)})`, error);
  }
};

/**
 * Ask for the microphone once and release it at once: the browser shows its
 * permission prompt here, before the user commits to joining, and device names
 * stay hidden until access is granted.
 */
export const requestMicrophoneAccess = async (): Promise<void> => {
  const stream = await openMicrophoneStream(true);
  stream.getTracks().forEach((track) => track.stop());
};

/** The microphones this browser can capture from; unnamed ones get a numbered name. */
export const listMicrophones = async (): Promise<MicrophoneOption[]> => {
  const devices = (await navigator.mediaDevices?.enumerateDevices?.()) ?? [];
  return devices
    .filter((device) => device?.kind === 'audioinput')
    .map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || COSELLER_MIC_COPY.unnamedMicrophone(index + 1),
    }));
};
